import { keepPreviousData, useQuery } from '@tanstack/react-query'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { centsToDollars } from './reconciliation-v2-api-client'

/**
 * Client for `GET /reconciliation-v2/reports/monthly-summary` and
 * `/annual-summary` (further down) — monthly: expected vs
 * actual (allocated) money per transaction source for one coverage month.
 * Wire money is INTEGER CENTS; mapped to dollars once here.
 */

export interface MonthlySummaryParams {
  year: number
  /** 1-12 */
  month: number
}

interface MonthlySummaryTotalsWire {
  benefitMonthCount: number
  unknownExpectedCount: number
  expectedCents: number
  actualCents: number
  differenceCents: number
}

interface MonthlySummaryRowWire extends MonthlySummaryTotalsWire {
  /** NULL = benefit-months due with no allocated payment. */
  source: string | null
}

interface MonthlySummaryResponseWire {
  year: number
  month: number
  periodStartDate: string
  rows: MonthlySummaryRowWire[]
  totals: MonthlySummaryTotalsWire
}

export interface MonthlySummaryTotals {
  benefitMonthCount: number
  /** Benefit-months whose expected price could not be resolved (counted as $0). */
  unknownExpectedCount: number
  expected: number
  actual: number
  /** actual − expected */
  difference: number
}

export interface MonthlySummaryRow extends MonthlySummaryTotals {
  source: string | null
}

export interface MonthlySummary {
  year: number
  month: number
  periodStartDate: string
  rows: MonthlySummaryRow[]
  totals: MonthlySummaryTotals
}

const mapTotals = ({
  expectedCents,
  actualCents,
  differenceCents,
  ...rest
}: MonthlySummaryTotalsWire): MonthlySummaryTotals => ({
  ...rest,
  expected: centsToDollars(expectedCents),
  actual: centsToDollars(actualCents),
  difference: centsToDollars(differenceCents),
})

/** Exported for unit tests. */
export const mapMonthlySummary = (
  wire: MonthlySummaryResponseWire
): MonthlySummary => ({
  year: wire.year,
  month: wire.month,
  periodStartDate: wire.periodStartDate,
  rows: wire.rows.map(({ source, ...totals }) => ({
    source,
    ...mapTotals(totals),
  })),
  totals: mapTotals(wire.totals),
})

export const getMonthlySummary = async (
  params: MonthlySummaryParams
): Promise<MonthlySummary> => {
  const response = await apiClient.get<MonthlySummaryResponseWire>(
    '/reconciliation-v2/reports/monthly-summary',
    { params }
  )
  return mapMonthlySummary(response.data)
}

export const useMonthlySummaryQuery = (params: MonthlySummaryParams) =>
  useQuery<MonthlySummary, Error>({
    queryKey: ['recon-v2-monthly-summary', params],
    queryFn: () => getMonthlySummary(params),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

// ── Annual summary ───────────────────────────────────────────────────────────

/**
 * A report column: a transaction source, or `Blank` (no assigned method).
 * Values match the API's `bySource` keys.
 */
export enum ReportSourceKey {
  Marqeta = 'marqeta',
  Wex = 'wex',
  IStream = 'i_stream',
  Check = 'check',
  GroupAch = 'group_ach',
  EePaid = 'ee_paid',
  Blank = 'blank',
}

interface SourceAmountsWire {
  bySource: Record<ReportSourceKey, number>
  totalCents: number
  count: number
}

interface AnnualSummaryMonthWire {
  month: number
  periodStartDate: string
  isFuture: boolean
  premiums: SourceAmountsWire
  paid: SourceAmountsWire
  unpaid: SourceAmountsWire
  newEnrollmentsUnpaid: SourceAmountsWire
}

interface AnnualSummaryResponseWire {
  year: number
  months: AnnualSummaryMonthWire[]
  yearTotals: {
    premiums: SourceAmountsWire
    paid: SourceAmountsWire
    unpaid: SourceAmountsWire
  }
  unreconciled: {
    rows: { source: string; amountCents: number; count: number }[]
    totalAmountCents: number
    totalCount: number
    absorbedCents: number
  }
}

/** Dollars per column, plus the row total and benefit-month count. */
export interface SourceAmounts {
  bySource: Record<ReportSourceKey, number>
  total: number
  /** Benefit-months behind the row (premiums / unpaid); 0 for paid. */
  count: number
}

export interface AnnualSummaryMonth {
  month: number
  periodStartDate: string
  /** After the current month — nothing is due yet. */
  isFuture: boolean
  premiums: SourceAmounts
  paid: SourceAmounts
  unpaid: SourceAmounts
  newEnrollmentsUnpaid: SourceAmounts
}

export interface AnnualSummary {
  year: number
  months: AnnualSummaryMonth[]
  yearTotals: {
    premiums: SourceAmounts
    paid: SourceAmounts
    unpaid: SourceAmounts
  }
  unreconciled: {
    rows: { source: string; amount: number; count: number }[]
    totalAmount: number
    totalCount: number
    absorbed: number
  }
}

const mapSourceAmounts = ({
  bySource,
  totalCents,
  count,
}: SourceAmountsWire): SourceAmounts => ({
  bySource: Object.fromEntries(
    Object.entries(bySource).map(([key, cents]) => [key, centsToDollars(cents)])
  ) as Record<ReportSourceKey, number>,
  total: centsToDollars(totalCents),
  count,
})

/** Exported for unit tests. */
export const mapAnnualSummary = (
  wire: AnnualSummaryResponseWire
): AnnualSummary => ({
  year: wire.year,
  months: wire.months.map((month) => ({
    month: month.month,
    periodStartDate: month.periodStartDate,
    isFuture: month.isFuture,
    premiums: mapSourceAmounts(month.premiums),
    paid: mapSourceAmounts(month.paid),
    unpaid: mapSourceAmounts(month.unpaid),
    newEnrollmentsUnpaid: mapSourceAmounts(month.newEnrollmentsUnpaid),
  })),
  yearTotals: {
    premiums: mapSourceAmounts(wire.yearTotals.premiums),
    paid: mapSourceAmounts(wire.yearTotals.paid),
    unpaid: mapSourceAmounts(wire.yearTotals.unpaid),
  },
  unreconciled: {
    rows: wire.unreconciled.rows.map(({ source, amountCents, count }) => ({
      source,
      amount: centsToDollars(amountCents),
      count,
    })),
    totalAmount: centsToDollars(wire.unreconciled.totalAmountCents),
    totalCount: wire.unreconciled.totalCount,
    absorbed: centsToDollars(wire.unreconciled.absorbedCents),
  },
})

export const getAnnualSummary = async (
  year: number
): Promise<AnnualSummary> => {
  const response = await apiClient.get<AnnualSummaryResponseWire>(
    '/reconciliation-v2/reports/annual-summary',
    { params: { year } }
  )
  return mapAnnualSummary(response.data)
}

export const useAnnualSummaryQuery = (year: number) =>
  useQuery<AnnualSummary, Error>({
    queryKey: ['recon-v2-annual-summary', year],
    queryFn: () => getAnnualSummary(year),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
